import { MAX_PREVIEWS_LOGGED_OUT } from '../../constants/labels';
import { shuffle } from 'lodash';
import { playableChatPreviews } from '../../utils/chatPreviewQueue';
import { previewTopicKey } from '../../utils/previewTopic';
import { buildTopicPreviewContinuation, TOPIC_OPENING_PREVIEW_COUNT, topicPreviewsFromGroups } from '../../utils/previewTopicQueue';
import axios from 'axios';
import {
    SET_NEXT_PREVIEWS_INFO,
    SET_SEARCH_TEXT,
    SET_SPEAKER_TOPICS,
    SET_TRACK,
} from '../types';


let nextPreviewQueueId = 0;
let nextRelatedPreviewRequestId = 0;
const previewQueueLimit = (authenticated) => authenticated ? 30 : MAX_PREVIEWS_LOGGED_OUT;

// Keep the selected clip within the queue cap, including signed-out sessions.
const cappedPreviewQueue = (previews, initialPreviewId, limit) => {
    const selectedIndex = Math.max(0, previews.findIndex(preview => preview.id === initialPreviewId));
    const start = Math.min(selectedIndex, Math.max(0, previews.length - limit));
    return previews.slice(start, start + limit);
};

export const restoreNextPreviewsInfo = (queue, initialPreviewId) => (dispatch, getState) => {
    if (!queue) return;
    const limit = queue.playbackSource === 'saved' ? queue.previewsInfo.length : previewQueueLimit(Boolean(getState().user?.authenticated));
    const previewsInfo = cappedPreviewQueue(queue.previewsInfo, initialPreviewId, limit);
    const playbackContextById = Object.fromEntries(previewsInfo
        .filter(preview => queue.playbackContextById?.[preview.id])
        .map(preview => [preview.id, queue.playbackContextById[preview.id]]));
    dispatch({ type: SET_NEXT_PREVIEWS_INFO, payload: {
        ...queue, previewsInfo, playbackContextById, initialPreviewId,
        relatedPreviewsAppended: queue.relatedPreviewsLoading ? false : queue.relatedPreviewsAppended,
        relatedPreviewsLoading: false,
        relatedPreviewsRequestId: null,
    } });
};

export const setNextPreviewsInfo = (authenticated, previewsInfo, options = {}) => (dispatch) => {
    if (previewsInfo) {
        const limit = options.playbackSource === 'saved' ? previewsInfo.length : previewQueueLimit(authenticated);
        const queuedPreviews = cappedPreviewQueue(previewsInfo, options.initialPreviewId, limit);
        const playbackTopic = options.playbackTopic ?? options.relatedTopicId;
        const topic = typeof playbackTopic === 'string' ? playbackTopic.trim() : '';
        const fromChatAnswer = options.playbackSource === 'chat';
        const responseIds = new Set(Array.isArray(options.responsePreviewIds) ? options.responsePreviewIds : previewsInfo.map(preview => preview.id));
        const playbackContextById = Object.fromEntries(queuedPreviews.map(preview => {
            if (topic) return [preview.id, { kind: 'topic', topic }];
            if (fromChatAnswer) {
                return [preview.id, { kind: responseIds.has(preview.id) ? 'answer' : 'moreHighlights' }];
            }
            return [preview.id, { kind: 'origin' }];
        }));
        dispatch({
            type: SET_NEXT_PREVIEWS_INFO,
            payload: {
                previewsInfo: queuedPreviews,
                queueId: ++nextPreviewQueueId,
                initialPreviewId: options.initialPreviewId,
                relatedPreviewId: options.playbackSource === 'saved' || options.allowRelatedPreviews === false ? null :
                    options.relatedPreviewId || (previewsInfo.length === 1 ? previewsInfo[0].id : null),
                relatedTopicId: options.relatedTopicId,
                playbackTopic: topic,
                topicPreviewCandidates: Array.isArray(options.topicPreviewCandidates) ? options.topicPreviewCandidates : [],
                playbackOrigin: options.playbackOrigin,
                ...(options.playbackSource === 'saved' ? { playbackSource: 'saved' } : {}),
                playbackContextById,
                ...(fromChatAnswer ? {
                    playbackSource: 'chat',
                    responsePreviewIds: [...new Set(queuedPreviews.map(preview => preview.id).filter(id => responseIds.has(id)))],
                } : {}),
                excludedPreviewIds: previewsInfo.map(preview => preview.id),
                relatedPreviewsAppended: false,
            },
        });
    } else {
        dispatch({
            type: SET_NEXT_PREVIEWS_INFO,
            payload: null,
        });
    }
}

// Start with up to eight clips including the selected one, then mix other
// topics. The catalog fills a short opening topic pool when possible.
export const appendRelatedPreviews = (authenticated, queueId, previewId, relatedTopicPreviews) => (dispatch, getState) => {
    const queue = getState().data.nextPreviewsInfo;
    if (!queue || queue.playbackSource === 'saved' || queue.queueId !== queueId || queue.relatedPreviewId !== previewId || queue.relatedPreviewsAppended) return;

    const topicKey = previewTopicKey(queue.relatedTopicId);
    const topics = Array.isArray(relatedTopicPreviews) ? relatedTopicPreviews : [];
    const requestId = ++nextRelatedPreviewRequestId;
    // Consume before requesting so rerenders cannot issue duplicate lookups.
    dispatch({ type: SET_NEXT_PREVIEWS_INFO, payload: {
        ...queue, relatedPreviewsAppended: true, relatedPreviewsRequestId: requestId,
    } });
    const append = (previews, openingIds = new Set()) => {
        const currentQueue = getState().data.nextPreviewsInfo;
        if (currentQueue?.queueId !== queueId || currentQueue.relatedPreviewId !== previewId || currentQueue.relatedPreviewsRequestId !== requestId) return;
        const currentIds = new Set([...currentQueue.excludedPreviewIds, ...currentQueue.previewsInfo.map(preview => preview.id)]);
        const uniquePreviews = playableChatPreviews(Array.isArray(previews) ? previews : [])
            .filter(preview => !currentIds.has(preview.id));
        const appendedPreviews = (topicKey ? uniquePreviews : shuffle(uniquePreviews))
            .slice(0, Math.max(0, previewQueueLimit(authenticated) - currentQueue.previewsInfo.length));
        const playbackContextById = { ...currentQueue.playbackContextById };
        const selectedTopicKey = previewTopicKey(currentQueue.playbackTopic) || topicKey;
        appendedPreviews.forEach(preview => {
            if (topicKey && openingIds.has(preview.id)) {
                playbackContextById[preview.id] = { kind: 'topic', topic: currentQueue.relatedTopicId.trim() };
            } else {
                playbackContextById[preview.id] = { kind: selectedTopicKey ? 'otherTopics' : 'moreHighlights' };
            }
        });
        dispatch({
            type: SET_NEXT_PREVIEWS_INFO,
            payload: {
                ...currentQueue,
                previewsInfo: currentQueue.previewsInfo.concat(appendedPreviews),
                playbackContextById,
                relatedPreviewsAppended: true,
                relatedPreviewsLoading: false,
            },
        });
    };

    if (!topicKey) {
        append(topics.flatMap(topic => Array.isArray(topic?.previews) ? topic.previews : []));
        return;
    }

    const excludedIds = [...queue.excludedPreviewIds, ...queue.previewsInfo.map(preview => preview.id)];
    const excluded = new Set(excludedIds);
    const primary = playableChatPreviews([
        ...(queue.topicPreviewCandidates || []),
        ...topicPreviewsFromGroups(queue.relatedTopicId, topics, excludedIds),
    ]).filter(preview => !excluded.has(preview.id));
    const focusedCount = queue.previewsInfo.filter(preview => queue.playbackContextById?.[preview.id]?.kind === 'topic').length;
    const openingSlots = Math.max(0, Math.min(TOPIC_OPENING_PREVIEW_COUNT - focusedCount, previewQueueLimit(authenticated) - queue.previewsInfo.length));
    const finishTopicQueue = (catalog = []) => {
        const candidates = playableChatPreviews(primary.concat(catalog)).filter(preview => !excluded.has(preview.id));
        const openingIds = new Set(candidates.slice(0, openingSlots).map(preview => preview.id));
        append(buildTopicPreviewContinuation(queue.relatedTopicId, candidates, topics, excludedIds, openingSlots), openingIds);
    };

    if (primary.length < openingSlots) {
        dispatch({ type: SET_NEXT_PREVIEWS_INFO, payload: { ...getState().data.nextPreviewsInfo, relatedPreviewsLoading: true } });
        return axios.get(`/topic_previews/${encodeURIComponent(queue.relatedTopicId)}`)
            .then(({ data }) => finishTopicQueue(previewTopicKey(data?.id) === topicKey && Array.isArray(data.previews) ? data.previews : []))
            .catch(() => finishTopicQueue());
    }
    finishTopicQueue();
};


export const setTrack = (track) => (dispatch) => {
    dispatch({
        type: SET_TRACK,
        payload: track,
    });
}


export const setSearchText = (text) => (dispatch) => {
    dispatch({
        type: SET_SEARCH_TEXT,
        payload: text,
    });
}


export const setSpeakerTopics = (speakerTopics) => (dispatch) => {
    dispatch({
        type: SET_SPEAKER_TOPICS,
        payload: speakerTopics,
    });
}
